import React, {
  useRef, useCallback, useContext, useEffect, useState, useMemo
} from 'react';
import { string, number, bool, shape } from 'prop-types';
import classNames from 'classnames';
import { BrowseSearchRedirector, DynamicComponent } from '@thd-nucleus/app-render';
import {
  client,
  extend,
  QueryProvider,
  QueryContext,
  hoist,
  params,
  shape as shapeType,
  string as stringType,
  bool as boolType,
  number as numberType,
} from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService, useStore } from '@thd-nucleus/experience-context';
import { HybridContentfulWidgets, useHybridPLP } from '@thd-olt-component-react/hybrid';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { useHelmet } from '@thd-nucleus/thd-helmet';
import { CustomerContext, useThdCustomer } from '@thd-olt-functional/customer-information';
import { ErrorBoundary } from '@thd-olt-component-react/error-boundary';
import { useNavigate, useLocation, useParams } from '@thd-olt-component-react/router';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import {
  ProductResults,
  ResultsApplied,
  ResultsFilterAndSort,
  ResultsFilterAndSortDrawer,
  ResultsHeader,
  ResultsPagination,
  ResultsWrapped,
  ResultsSortBy,
  ResultsDimensions,
  ResultsToggleLayout,
  useProductResults,
  productDefaultResultsDataModel,
  updateQueryParams,
  parseUrl,
  storeLifecycleEvent,
  ProductResultsContext
} from '@thd-olt-component-react/product-results';
import { ProductPodPlaceholder } from '@thd-olt-component-react/product-pod';
import {
  ProductPodGroup,
  CustomProductPodDetails
} from '@thd-olt-component-react/product-pod-group';
import { RectShape, TextBlock } from '@thd-olt-component-react/loading-placeholder';
import { Breadcrumbs } from '@thd-olt-component-react/breadcrumbs';
import { CategoryCards } from '@thd-olt-component-react/category-cards';
import { SearchFeedback } from '@thd-olt-component-react/search-feedback';
import { RelatedProducts, RelatedSearch } from '@thd-olt-component-react/related-search';
import { EmtQuestionsAndAnswers } from '@thd-olt-component-react/emt-generated-content';
import { ProductCategoryVisnav } from '@thd-olt-component-react/product-category-visnav';
import { debounce } from '@thd-olt-functional/utils';
import {
  DiscoveryZonesWrapper,
  PlpRecsRenderPlaceholder,
  PlpRvRenderPlaceholder,
  BuyItAgain
} from '@thd-olt-component-react/thd-recs-containers';
import {
  SponsoredBanner,
  SponsoredSkyscraperBanner,
  SponsoredInGridBanner
} from '@thd-olt-component-react/sponsored-content';
import { RetailMediaCarousel } from '@thd-olt-component-react/retail-media-pla-sponsored-placements';
import { Metadata, BrowseSearchMetadata } from '@thd-olt-component-react/metadata';
import { CompareTray } from '@thd-olt-component-react/compare';
import { AisleBay } from '@thd-olt-component-react/aisle-bay';
import { NoResultsFound } from '@thd-olt-component-react/no-results-found';
import { Hydrator, declareContexts } from '@thd-olt-component-react/hydrator';
import { FitCompatibility } from '@thd-olt-component-react/fit-compatibility';
import { useTheme } from '@thd-olt-component-react/theme-provider';
import {
  getItemIds,
  getIsEtchVisNavOnPLP,
  onStoreChange,
  impProductPodData,
  impPLPRecs,
  imagePreloader,
  isRefinedPage,
  isNIncluded,
  getServerPageSize
} from './browse-and-search-utils';
import {
  EmtGeneratedContentLoad,
  EmtGeneratedContent,
  EmtGeneratedContentModel
} from './dynamic-imports';
import { EmtLinksLoad, EmtLinks, EmtLinksModel } from '../shared-dynamic-imports';
import {
  CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER,
  HYDRATION_STANDARD_SCROLL_BUFFER,
  MAX_PODS_TO_HYDRATE,
  RECS_REQUEST_KEY,
  CATEGORY_CARD_POSITION_INDEX,
  PRODUCT_POD_COUNT,
  DEFAULT_LAYOUT_STYLE,
  CHECK_CAT_STYLE
} from '../../constants';
import {
  getHasInStoreFilter,
  updateStickyHeader,
  updateHeaderConfiguration,
  getIsRefrigeratorPage
} from '../utils';
import { useCompare } from '../../hooks/useCompare';
import { usePrefetchPip } from '../../hooks/usePrefetchPip';
import { useAdditionalSearchParams } from '../../hooks/useAdditionalSearchParams';
import { getCategoryCards } from '../../utils/getCategoryCards';
import '../../styles/browse-search.scss';

import { CustomProductPodResponsive } from './CustomProductPodResponsive';
import { CustomProductPodTwoByTwoColumn } from './CustomProductPodTwoByTwoColumn';

const HoistedProductPodGroup = hoist(
  ProductPodGroup, CustomProductPodResponsive, CategoryCards, CustomProductPodDetails);

export const BrowseAndSearch = ({
  pageType,
  prefetchPip,
  productPodHoverDelay,
  isLocalized,
  storeId,
  storeZip,
  pathname,
  instance,
  customerType
}) => {
  /* CONTEXTS */
  declareContexts([ProductResultsContext, QueryContext, ExperienceContext, CustomerContext]);
  const thdCustomer = useThdCustomer();
  const { isCustomerIdentified = false } = useThdCustomer();
  let navigate = useNavigate();
  const { clientStore, deliveryZip: serverDeliveryZip } = useContext(ExperienceContext);
  const { isClientResolved } = useContext(QueryContext);

  const theme = useTheme(BrowseAndSearch);
  const {
    hideSponsoredCarousel, hideSponsoredBanner, hideSponsoredSkyscraperBanner, hideFFMFilters,
    hideFeedback
  } = theme.props;

  /* VARIABLES */
  const isB2BCustomer = customerType === 'b2b';
  const { search } = useLocation();
  const path = `${pathname.replace(/render-\w+\//, '')}`;
  const pathWithParams = `${path}${search}`;
  const checkShowProduct = CHECK_CAT_STYLE.test(pathWithParams);
  const isSearch = pageType === 'search';
  const isBrowse = pageType === 'browse';
  const hideOnNoCategory = /Ntt-/.test(pathWithParams);
  const disableSSR = typeof window !== 'undefined' && window.DISABLE_SSR;
  const varnish = !disableSSR && !isSearch;
  const store = useStore({ varnish, online: false });
  const loadedStore = useRef(store);
  const localizedAndClientLoaded = useRef(false);
  const initialLoadedStoreId = useRef(storeId);
  const resultsDimensionsRef = useRef(null);
  const searchNavPropsRef = useRef({
    pageSize: disableSSR ? PRODUCT_POD_COUNT.CLIENT : getServerPageSize({ isSearch, isBrowse }),
    askForSponsored: isSearch,
    deliveryZip: serverDeliveryZip
  });
  const sortValue = useRef('bestmatch:asc');
  const isDiscoveryZonesEnabled = useConfigService('fs-prop:isDiscoveryZonesEnabled');
  const isDiscoveryZonesSearchEnabled = useConfigService('fs-prop:isDiscoveryZonesSearchEnabled');
  const viewAllFilterButton = useConfigService('fs-prop:isViewAllFilterButtonEnabled');
  const viewPrimaryFilters = useConfigService('fs-prop:isviewPrimaryFiltersEnabled');
  const isFulfillmentRedesignEnabled = useConfigService('fs-prop:isFulfillmentRedesignEnabled');
  const viewVizNavFilters = useConfigService('fs-prop:isViewVizNavFiltersEnabled');
  const isGetItFastOnPlp = useConfigService('fs-prop:isGetItFastOnPlp');
  const isInGridBannerEnabled = useConfigService('fs-prop:isInGridBannerEnabled') || false;
  const swapPrimaryFiltersGetItFastEnabled = useConfigService('fs:swapPrimaryFiltersGetItFastEnabled');
  const isFulfillmentFSEnabled = useConfigService('fs:isFulfillmentFilterAndMsgEnabled') || false;
  const isViewSimilarTopButtonEnabled = useConfigService('fs:isViewSimilarButtonPositionAboveABTest');
  const isViewSimilarBottomButtonEnabled = useConfigService('fs:isViewSimilarButtonPositionBelowABTest');

  const resultsColProps = isGetItFastOnPlp
    ? { xs: '12', sm: '12', md: '12', lg: '12' }
    : { xs: '8', sm: '9', md: '10', lg: '10' };

  const filterBrowseRecs = useConfigService('fs:browseFilteredRecs');
  const viewListPods = useConfigService('fs-prop:isViewListPodsEnabled');
  const viewResultsToggle = useConfigService('fs-prop:isToggleResultsEnabled');

  const ffmTheme = viewResultsToggle ? '11' : '12'
  const defaultLayoutStyle = viewListPods ? 'List' : 'Grid';

  /* STATE */
  const [isHydrated, setIsHydrated] = useState(false);
  const [layoutStyle, setLayoutStyle] = useState(defaultLayoutStyle);

  const [inGridBannerIndex, setInGridBannerIndex] = useState(null);
  const [isInGridBannerBottom, setIsInGridBannerBottom] = useState(false);

  // Discovery Zones state management for dynamic placement and rendering
  const [containerIndex, setContainerIndex] = useState({ zone1: 8, zone2: 24 });
  const [isDiscoveryZonesLoaded, setIsDiscoveryZonesLoaded] = useState(false);
  const [discoveryZonesFallback, setDiscoveryZonesFallback] = useState(false);

  // Combine these flags into a single condition we can check more easily.
  const isDiscoveryZonesEligible = (isBrowse && isDiscoveryZonesEnabled)
    || (isSearch && isDiscoveryZonesSearchEnabled);

  if (isLocalized) {
    let deliveryZipCode;
    if (typeof window !== 'undefined') {
      deliveryZipCode = window.THD_LOCALIZER_AUTO_INIT?.Localizer?.getDeliveryZipcodeDetails() || {};
    }

    searchNavPropsRef.current = {
      pageSize: PRODUCT_POD_COUNT.CLIENT,
      askForSponsored: true,
      deliveryZip: deliveryZipCode.zipcode
    };
  }

  /* CALLBACKS */
  const onDimensionsChange = ({ refinement, onDisplay }) => {
    if (!viewPrimaryFilters) {
      window.LIFE_CYCLE_EVENT_BUS.trigger('filters-refinements.click', refinement);
    } else if (refinement?.dimensionName === 'Get It Fast') {
      storeLifecycleEvent('filters-refinements-visualNav.click', refinement);
    }
    const updatedPath = updateQueryParams(refinement.url, [
      {
        paramName: 'onDisplay',
        paramValue: onDisplay,
      }
    ]);
    navigate(updatedPath);
  };

  // TODO: This fcn needs to be combined with onMultiStoreChange to preserve both params. Both features are co-dependent
  const onDisplayChange = ({ useStoreFilter }) => {
    const updatedPath = updateQueryParams(pathWithParams, [
      { paramName: 'onDisplay', paramValue: useStoreFilter }
    ]);
    navigate(updatedPath);
  };

  const onPaginate = ({ url }) => {
    navigate(url);

    const offsetTop = resultsDimensionsRef?.current?.parentElement?.parentElement?.offsetTop || 0;
    window.scrollTo(0, offsetTop);
  };

  const onSortChange = ({
    sortby, sortorder, name, currentPage, page, updatedPath = null
  }) => {
    const newPath = updateQueryParams(updatedPath || pathWithParams, [
      { paramName: 'Nao', paramValue: null },
      { paramName: 'sortorder', paramValue: sortorder },
      { paramName: 'sortby', paramValue: sortby },
    ]);
    window.LIFE_CYCLE_EVENT_BUS.trigger('sort-refinements.click', {
      name,
      currentPage,
      page
    });
    navigate(newPath);
  };

  const handleDiscoveryZonesOnLoad = (data) => {
    if (data && data.componentName === 'SearchViewed') {
      setDiscoveryZonesFallback(true);
    }
  };

  const onSortBy = (value) => {
    sortValue.current = value;
  };

  const {
    keyword,
    lowerbound,
    Nao = 0,
    sortby,
    nValue = '',
    sortorder,
    upperbound,
    onDisplay
  } = parseUrl({ path: pathWithParams });

  // For hybrid-contentful-plp
  const shouldSkipHybridPlp = isSearch || isRefinedPage(pathname);
  const hybridParams = {
    slug: isNIncluded(nValue) ? nValue : 'N-' + nValue,
    customerType,
    instance,
    storeId,
    skip: shouldSkipHybridPlp,
    lazy: shouldSkipHybridPlp,
    isShowProducts: checkShowProduct
  };
  const { data: hybridData, contentElements, loading: hybridLoading } = useHybridPLP(hybridParams);
  useHelmet('major-appliance', { hybridData }, imagePreloader, [hybridData]);
  const layouts = hybridData?.layouts;
  const layoutID = layouts?.id; // for impressions
  const isHybridLayout = layoutID && layouts?.content?.__typename === 'HybridPLPLayout'; // for validation
  const isBrandPage = layouts?.content?.pageType === 'Brand Page';

  const handleProductClick = useCallback((evt, product, action, sponsoredClickValues) => {
    let canonicalUrl = product?.identifiers?.canonicalUrl;

    if (evt.ctrlKey || evt.metaKey) {
      return;
    }

    if (canonicalUrl) {
      evt.preventDefault();
      navigate(canonicalUrl);
    }
  }, [navigate]);

  /* HOOKS */
  const { selectedForCompare, onSelectedForCompare, onCompare } = useCompare();

  const additionalSearchParams = useAdditionalSearchParams({ pathWithParams, searchNavPropsRef });

  const response = useProductResults({
    additionalSearchParams,
    keyword,
    lowerbound,
    nValue,
    pageSize: searchNavPropsRef.current.pageSize,
    storeId,
    storefilter: onDisplay === 'true' ? 'IN_STORE' : 'ALL',
    start: Nao,
    sortby,
    sortorder,
    upperbound,
    isCustomerIdentified: isLocalized ? isCustomerIdentified : false,
    shouldUpdateCustomContextProp: false
  });

  const {
    data, error, loading, variables, responseLoading
  } = response || {};

  const hasCategoryCard = data?.searchModel?.orangeGraph?.universalSearchArray.length > 0;
  const isExperienceResolved = isClientResolved({ queryName: 'searchModel' });

  const isBuyitagainEnabled = useConfigService('fs-prop:isBuyitagainEnabled');
  const { products = [], metadata = {}, searchReport = {}, appliedDimensions = [] } = data?.searchModel || {};

  const hasInStoreFilter = getHasInStoreFilter(appliedDimensions);

  useEffect(() => {
    const onResize = debounce(200, () => {
      if (isExperienceResolved) {
        // 4 pod layout
        if (window.innerWidth >= 1280) {

          if (hasCategoryCard) {
            if (products?.length > 24) {
              setInGridBannerIndex(24);
              setIsInGridBannerBottom(false);
            } else if (products?.length <= 24) {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
          if (!hasCategoryCard) {
            if (products.length > 12) {
              setInGridBannerIndex(12);
              setIsInGridBannerBottom(false);
            } else {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
          // 3 pod layout
        } else if (window.innerWidth >= 1024) {
          if (hasCategoryCard) {
            if (products?.length > 18) {
              setInGridBannerIndex(18);
              setIsInGridBannerBottom(false);
            } else {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
          if (!hasCategoryCard) {
            if (products.length > 9) {
              setInGridBannerIndex(9);
              setIsInGridBannerBottom(false);
            } else {
              setIsInGridBannerBottom(true);
              setInGridBannerIndex(null);
            }
          }
        }
      }
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }

    onResize();

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, [isExperienceResolved]);

  const isCategory = metadata?.contentType === 'categorypage';
  const isAppliedRefinementsAvailable = ((data?.searchModel?.appliedDimensions
    && data.searchModel.appliedDimensions.length > 0)) || onDisplay === 'true';
  if (data && !loading && !response.responseLoading && isLocalized) {
    localizedAndClientLoaded.current = true;
    loadedStore.current = {
      storeId: variables.storeId,
      zipCode: variables.zipCode,
    };
  }

  const handleLayoutToggle = (style) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('PLP_LAYOUTSTYLE', style);
    }
    setLayoutStyle(style);
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && viewListPods) {
      setLayoutStyle(window.localStorage.getItem('PLP_LAYOUTSTYLE') || layoutStyle);
    }
  }, []);

  useEffect(() => {
    updateStickyHeader('Header3-sticky-desktop');
    updateHeaderConfiguration('sticky');
    window.LIFE_CYCLE_EVENT_BUS.on('product-results.change-store', onStoreChange);
    return () => window.LIFE_CYCLE_EVENT_BUS.off('product-results.change-store', onStoreChange);
  }, []);

  useEffect(() => {
    if (data && !loading && isExperienceResolved) {
      const { stores: storeInfo, productCount = {} } = metadata;

      const receivedAllProducts = clientStore?.storeId === variables?.storeId && !!storeInfo;
      const hasNoProducts = productCount?.allProducts === '0';
      // eslint-disable-next-line max-len
      const shouldTriggerBrowseAndCategoryAnalytics = isCategory || (isBrowse && (receivedAllProducts || hasNoProducts));

      const analyticsSearchModel = {
        ...data?.searchModel,
        defaultPageSize: searchNavPropsRef.current.pageSize,
        isLocalized,
        pageID: isNIncluded(nValue) ? nValue : 'N-' + nValue,
        isHybridContentful: isHybridLayout
      };
      if (shouldTriggerBrowseAndCategoryAnalytics) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('browse.new-products', analyticsSearchModel);
      } else if (isSearch && variables?.pageSize !== 12) {
        LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('search.new-products', analyticsSearchModel);
      }
    }
  }, [getItemIds(products).join('-'), metadata.stores?.storeId, isLocalized]);

  useEffect(() => {
    const onResize = debounce(200, () => {
      if (typeof window !== 'undefined') {
        if (window.innerWidth >= 1280) {
          setContainerIndex({ zone1: 8, zone2: 16 });
        } else if (window.innerWidth >= 1024) {
          setContainerIndex({ zone1: 6, zone2: 12 });
        } else if (window.innerWidth >= 640) {
          setContainerIndex({ zone1: 4, zone2: 8 });
        }
      }
    });
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onResize);
    }

    onResize();
    setIsDiscoveryZonesLoaded(true);
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onResize);
      }
    };
  }, []);

  const { prefetchProductInfo } = usePrefetchPip(prefetchPip, varnish, storeZip);

  const mountedFn = ({ mounted }) => {
    if (!isLocalized) return false;
    if (response.responseLoading) return false;
    return mounted;
  };

  const skipFn = ({ skip, queryName, attributes }) => {
    if (queryName !== 'product') {
      return skip;
    }
    const isResolved = isClientResolved({ queryName: 'searchModel' });
    if (attributes.product.fulfillment) {
      if (isResolved) {
        return skip;
      }
      return true;
    }
    return skip;
  };

  const isEtchVisNavInPLP = getIsEtchVisNavOnPLP(data, isB2BCustomer);
  const isDynamicBrandPage = data?.searchModel?.aisles?.length > 0;
  // Filters category cards from the query response so B2B users only see pro content cards
  const dataForCategoryCards = getCategoryCards(data, isB2BCustomer);
  const browseSearchClass = classNames('sui-grid sui-gap-2 sm:sui-grid-cols-2',
    {
      'lg:sui-grid-cols-1 xl:sui-grid-cols-2': (layoutStyle === 'List'),
      'lg:sui-grid-cols-3 xl:sui-grid-cols-4 sui-mb-2': (layoutStyle !== 'List')
    });

  const wrapperClass = classNames({
    'sui-col-span-2': (layoutStyle === 'List'),
    'sm:sui-col-span-2 lg:sui-col-span-3 xl:sui-col-span-4': (layoutStyle !== 'List')
  });

  const categoryCardClass = classNames('sm:sui-col-span-2',
    {
      'lg:sui-col-span-2 xl:col-span-2': (layoutStyle === 'List'),
      'lg:sui-col-span-3 xl:sui-col-span-4': (layoutStyle !== 'List')
    });

  const carouselClasses = classNames('sui-pb-4 sui-pl-2', {
    'sui-col-span-2': (layoutStyle === 'List'),
    'sm:sui-col-span-2 lg:sui-col-span-3 xl:sui-col-span-4': (layoutStyle !== 'List')
  });

  const renderDiscoveryZones = (zoneId) => {
    return (
      <div className={wrapperClass}>
        <DiscoveryZonesWrapper
          keyword={keyword}
          data={data}
          loading={response.responseLoading}
          nValue={nValue}
          schemaName="discoveryZones"
          storeId={loadedStore.current?.storeId}
          zoneId={zoneId}
          errorBoundary
          pageType={pageType}
          itemIds={getItemIds(products)}
          onLoad={handleDiscoveryZonesOnLoad}
          hydrator={{
            className: 'sui-bg-primary sui-grid sui-mb-4',
            delay: 1500,
            scrollBuffer: 500,
            id: `discovery-zones-${zoneId}`,
            immediateRender: isDiscoveryZonesLoaded
          }}
          dynamic={{
            pageType
          }}
        />
      </div>
    );
  };

  const productsReduce = (productPods) => {
    let groupOfPods = 0;
    let hydrationChunks = productPods?.reduce((hydrator, product) => {
      if (!hydrator[groupOfPods]) {
        hydrator.push([]);
      }
      if (hydrator[groupOfPods].length < MAX_PODS_TO_HYDRATE.DESKTOP) {
        hydrator[groupOfPods].push(product);
        if (hydrator[groupOfPods].length === MAX_PODS_TO_HYDRATE.DESKTOP) {
          groupOfPods += 1;
        }
      }
      return hydrator;
    }, []);

    if (productPods?.length === PRODUCT_POD_COUNT.SERVER
      && !isClientResolved({ queryName: 'searchModel' })) {
      const placeholderHydrators = (PRODUCT_POD_COUNT.CLIENT - PRODUCT_POD_COUNT.SERVER) / MAX_PODS_TO_HYDRATE.DESKTOP;
      for (let i = 0; i < placeholderHydrators; i += 1) {
        hydrationChunks.push(Array(MAX_PODS_TO_HYDRATE.DESKTOP).fill());
      }
    }

    return hydrationChunks;
  };

  const originalProducts = useRef(productsReduce(products));
  const hydrators = isHydrated || !originalProducts.current?.length
    ? productsReduce(products)
    : originalProducts.current;

  if (isSearch && searchReport?.totalProducts === 0 && !!keyword && !loading) {
    return (
      <NoResultsFound
        keyword={keyword}
        requestKey={RECS_REQUEST_KEY}
        searchData={data}
        searchError={error}
        searchLoading={loading}
        storeId={initialLoadedStoreId.current}
        isCategory={isCategory}
        pageType={pageType}
      />
    );
  }

  const hasDeliveryFilter = (breadCrumbs, filterLabelList) => {
    return (breadCrumbs || []).filter((breadCrumbDetail) => filterLabelList.includes(breadCrumbDetail?.label));
  };

  const applicableFilters = ['Same-Day Delivery', 'Next-Day Delivery', 'Free 1-2 Day Delivery'];
  const hasSameDayFilter = !!hasDeliveryFilter(data?.searchModel?.taxonomy?.breadCrumbs, applicableFilters).length;
  const isRefrigeratorPage = getIsRefrigeratorPage(data);

  return (
    <>
      <BrowseSearchRedirector variables={variables} />
      <Row className="isBound browse-search">
        <ErrorBoundary name="browse-and-search-metadata">
          <Metadata>
            <BrowseSearchMetadata
              data={data}
              loading={loading}
              error={error}
              variables={variables}
              seoGeneratedContentVariables={{ navParam: variables?.navParam }}
              pathWithQueryParams={pathWithParams}
            />
          </Metadata>
        </ErrorBoundary>
        <Row className="u--paddingTop u--paddingBottom">
          <Col flatten>
            <ErrorBoundary name="breadcrumbs">
              <div className={isHybridLayout && 'sui-ml-2'}>
                <Breadcrumbs
                  data={data}
                  loading={loading}
                  error={error}
                  hideOnNoCategory={hideOnNoCategory}
                />
              </div>
            </ErrorBoundary>
          </Col>
        </Row>
        <ProductResults data={data} loading={loading} error={error}>
          {!hideSponsoredBanner && (
            <Row>
              <Col>
                <div className={isHybridLayout && 'sui-ml-2'}>
                  <ErrorBoundary name="sponsored-banner-nucleus">
                    <SponsoredBanner
                      browserId={thdCustomer?.mcvisID}
                      pageContext={{
                        label: 'browse-search',
                        data,
                        keyword,
                        isCategory,
                        isBrowse,
                        isSearch
                      }}
                      avoidPreload
                      showSponsoredBanner={isB2BCustomer}
                    />
                  </ErrorBoundary>
                </div>
              </Col>
            </Row>
          )}
          <Row>
            <Col flatten>
              <div className={isHybridLayout && 'sui-ml-2'}>
                <ErrorBoundary name="results-header">
                  <ResultsHeader
                    showShopAllLink={isDynamicBrandPage}
                    resultsRef={resultsDimensionsRef}
                  />
                </ErrorBoundary>
              </div>
            </Col>
          </Row>
          {isHybridLayout && !hybridLoading && isBrowse && (
            <Col>
              <ErrorBoundary name="contentful-widgets">
                <HybridContentfulWidgets
                  contentElements={contentElements}
                  loading={hybridLoading}
                  isBrowse={isBrowse}
                />
              </ErrorBoundary>
            </Col>
          )}
          {!isEtchVisNavInPLP && viewVizNavFilters && !isBrandPage && (
            <div
              name="product-category-visnav-container"
              className="sui-mt-2 sui-mb-5 sui-mx-2 sui-w-full empty:sui-p-0"
            >
              <ErrorBoundary name="product-category-visnav-container">
                <ProductCategoryVisnav data={data} />
              </ErrorBoundary>
            </div>
          )}
          {!isCategory && !viewPrimaryFilters && (
            <Row>
              <Col xs={viewResultsToggle ? '8' : '9'}>
                <ErrorBoundary name="results-applied">
                  <ResultsApplied
                    onAppliedChange={onDimensionsChange}
                    onDisplay={onDisplay}
                    onDisplayChange={onDisplayChange}
                    isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                    desktopSort={sortValue.current}
                  />
                </ErrorBoundary>
              </Col>
              <Col className="sui-py-0" xs="3">
                <ErrorBoundary name="results-sort-by">
                  <ResultsSortBy
                    onSortChange={onSortChange}
                    oldDesign
                  />
                </ErrorBoundary>
              </Col>
              {viewResultsToggle && (
                <Col xs="1" className="layout-toggle">
                  <ErrorBoundary name="results-toggle-layout">
                    <ResultsToggleLayout
                      activeLayout={layoutStyle}
                      toggleActiveLayout={handleLayoutToggle}
                    />
                  </ErrorBoundary>
                </Col>
              )}
            </Row>
          )}

          {!isCategory && (
            <Row className="browse-search__left-outer-padding">
              {!isGetItFastOnPlp && (
                <Col xs="4" sm="3" md="2" lg="2" flatten>
                  {!hideFFMFilters && (
                    <Hydrator
                      id="browse-search-dimensions"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    >
                      <ResultsDimensions
                        onDimensionsChange={onDimensionsChange}
                        onDisplay={onDisplay}
                        onDisplayChange={onDisplayChange}
                        shiftCategoryDimension={isDynamicBrandPage}
                        showOnlyGetItFast={viewAllFilterButton && viewPrimaryFilters}
                        isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                      />
                    </Hydrator>
                  )}
                  {!isInGridBannerEnabled && (
                    <SponsoredSkyscraperBanner
                      browserId={thdCustomer?.mcvisID}
                      pageContext={{
                        label: 'browse-search',
                        data,
                        keyword,
                        isCategory,
                        isBrowse,
                        isSearch
                      }}
                      dynamic={{
                        pageType
                      }}
                      hydrator={{
                        className: 'grid',
                        scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                      }}
                    />
                  )}
                </Col>
              )}
              {isBuyitagainEnabled && (
                <ErrorBoundary>
                  <div className="sui-grid sui-w-full sui-ml-3">
                    <BuyItAgain
                      nValue={nValue}
                      schemaName="buyitagain"
                      anchorId={`N-${nValue}`}
                      loading={responseLoading}
                      data={data}
                      error={error}
                      showLoading
                    />
                  </div>
                </ErrorBoundary>
              )}
              {isGetItFastOnPlp && (
                <Row className="browse-search--no-left-margin ">
                  <Col className="browse-search--no-bottom-padding" xs="9">
                    <ErrorBoundary name="results-applied">
                      <ResultsApplied
                        onAppliedChange={onDimensionsChange}
                        onDisplay={onDisplay}
                        onDisplayChange={onDisplayChange}
                        viewPrimaryFilters={viewPrimaryFilters}
                        isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                        isGetItFastOnPlp={isGetItFastOnPlp}
                      />
                    </ErrorBoundary>
                  </Col>
                </Row>
              )}
              <Col
                className="browse-search__pod-col-no-padding"
                /* eslint-disable-next-line react/jsx-props-no-spreading */
                {...resultsColProps}
              >
                {!swapPrimaryFiltersGetItFastEnabled ? (
                  <>
                    {viewPrimaryFilters && (
                      <>
                        <ErrorBoundary name="filter-and-sort-drawer">
                          <ResultsFilterAndSortDrawer
                            onSortChange={onSortChange}
                            onDimensionsChange={onDimensionsChange}
                            disableStickyFilter
                            excludePickUpToday
                            hideViewAll={!viewAllFilterButton}
                            onDisplay={onDisplay}
                            onDisplayChange={onDisplayChange}
                            desktopSort={sortValue.current}
                          />
                        </ErrorBoundary>
                      </>
                    )}
                  </>
                )
                  : (
                    <>
                      {(viewPrimaryFilters) && (
                        <>
                          {viewPrimaryFilters && isGetItFastOnPlp && (
                            <>
                              <div className="sui-flex sui-pt-5">
                                {!hideFFMFilters && (
                                  <Col
                                    className="sui-pb-0"
                                    xs={viewResultsToggle ? '8' : '9'}
                                  >
                                    <ErrorBoundary name="results-get-it-fast">
                                      <ResultsDimensions
                                        onDimensionsChange={onDimensionsChange}
                                        onDisplay={onDisplay}
                                        onDisplayChange={onDisplayChange}
                                        shiftCategoryDimension={isDynamicBrandPage}
                                        isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                        showOnlyGetItFast
                                        isGetItFastOnPlp={isGetItFastOnPlp}
                                        viewPrimaryFilters={viewPrimaryFilters}
                                      />
                                    </ErrorBoundary>
                                  </Col>
                                )}
                                <Col className="sui-py-0" xs={hideFFMFilters ? ffmTheme : '3'}>
                                  <ErrorBoundary name="results-sort-by">
                                    <ResultsSortBy
                                      onSortChange={onSortChange}
                                      oldDesign
                                    />
                                  </ErrorBoundary>
                                </Col>
                                {viewResultsToggle && (
                                  <Col xs="1" className="layout-toggle">
                                    <ErrorBoundary name="results-toggle-layout">
                                      <ResultsToggleLayout
                                        activeLayout={layoutStyle}
                                        toggleActiveLayout={handleLayoutToggle}
                                      />
                                    </ErrorBoundary>
                                  </Col>
                                )}
                              </div>
                            </>
                          )}
                        </>
                      )}
                    </>
                  )}

                {!swapPrimaryFiltersGetItFastEnabled ? (
                  <>
                    {(viewPrimaryFilters || isBuyitagainEnabled) && (
                      <>
                        {viewPrimaryFilters && isGetItFastOnPlp ? (
                          <>
                            <Row className="browse-search--no-left-margin sui-pb-3">
                              {!hideFFMFilters && (
                                <Col
                                  className="browse-search--top-bottom-padding sui-pb-0"
                                  xs={viewResultsToggle ? '8' : '9'}
                                >
                                  <ErrorBoundary name="results-get-it-fast">
                                    <ResultsDimensions
                                      onDimensionsChange={onDimensionsChange}
                                      onDisplay={onDisplay}
                                      onDisplayChange={onDisplayChange}
                                      shiftCategoryDimension={isDynamicBrandPage}
                                      isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      showOnlyGetItFast
                                      isGetItFastOnPlp={isGetItFastOnPlp}
                                      viewPrimaryFilters={viewPrimaryFilters}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              )}
                              <Col className="sui-py-0" xs={hideFFMFilters ? ffmTheme : '3'}>
                                <ErrorBoundary name="results-sort-by">
                                  <ResultsSortBy
                                    onSortChange={onSortChange}
                                    oldDesign
                                  />
                                </ErrorBoundary>
                              </Col>
                              {viewResultsToggle && (
                                <Col xs="1" className="layout-toggle">
                                  <ErrorBoundary name="results-toggle-layout">
                                    <ResultsToggleLayout
                                      activeLayout={layoutStyle}
                                      toggleActiveLayout={handleLayoutToggle}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              )}
                            </Row>
                            {isAppliedRefinementsAvailable && (
                              <Row className="browse-search--no-left-margin">
                                <Col>
                                  <ErrorBoundary name="top-results-applied">
                                    <ResultsApplied
                                      onAppliedChange={onDimensionsChange}
                                      onDisplay={onDisplay}
                                      onDisplayChange={onDisplayChange}
                                      hideResultsCount={viewPrimaryFilters}
                                      isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      isGetItFastOnPlp={isGetItFastOnPlp}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              </Row>
                            )}
                          </>
                        ) : (
                          <>
                            {isAppliedRefinementsAvailable && (
                              <Row className="browse-search--no-left-margin">
                                <Col>
                                  <ErrorBoundary name="top-results-applied">
                                    <ResultsApplied
                                      onAppliedChange={onDimensionsChange}
                                      onDisplay={onDisplay}
                                      onDisplayChange={onDisplayChange}
                                      hideResultsCount={viewPrimaryFilters}
                                      isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                      isGetItFastOnPlp={isGetItFastOnPlp}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              </Row>
                            )}
                            {isBrowse && !isGetItFastOnPlp && (
                              <Row className="browse-search--no-left-margin sui-mt-2 sui-mb-6">
                                <Col>
                                  <ErrorBoundary name="fit-compatibility">
                                    <FitCompatibility
                                      type="PLP"
                                      imageCacheEnabled
                                      navParam={nValue}
                                      data={data}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              </Row>
                            )}
                            <Row className="browse-search--no-left-margin">
                              <Col xs={viewResultsToggle ? '8' : '9'}>
                                <ErrorBoundary name="results-applied">
                                  <ResultsApplied
                                    onAppliedChange={onDimensionsChange}
                                    onDisplay={onDisplay}
                                    onDisplayChange={onDisplayChange}
                                    viewPrimaryFilters={viewPrimaryFilters}
                                    isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                  />
                                </ErrorBoundary>
                              </Col>
                              <Col className="sui-py-0" xs="3">
                                <ErrorBoundary name="results-sort-by">
                                  <ResultsSortBy
                                    onSortChange={onSortChange}
                                    oldDesign
                                  />
                                </ErrorBoundary>
                              </Col>
                              {viewResultsToggle && (
                                <Col xs="1" className="layout-toggle">
                                  <ErrorBoundary name="results-toggle-layout">
                                    <ResultsToggleLayout
                                      activeLayout={layoutStyle}
                                      toggleActiveLayout={handleLayoutToggle}
                                    />
                                  </ErrorBoundary>
                                </Col>
                              )}
                            </Row>
                          </>
                        )}
                      </>
                    )}
                  </>
                )
                  : (
                    <>
                      <div
                        className={classNames(
                          'sui-flex sui-mx-2 sui-pt-5',
                          {
                            'sui-pb-1': isAppliedRefinementsAvailable || isRefrigeratorPage,
                            'sui-pb-5': !isAppliedRefinementsAvailable && !isRefrigeratorPage,
                          }
                        )}
                      >
                        {viewPrimaryFilters && (
                          <>
                            <ErrorBoundary name="filter-and-sort-drawer">
                              <ResultsFilterAndSortDrawer
                                onSortChange={onSortChange}
                                onDimensionsChange={onDimensionsChange}
                                disableStickyFilter
                                excludePickUpToday
                                hideViewAll={!viewAllFilterButton}
                                onDisplay={onDisplay}
                                onDisplayChange={onDisplayChange}
                                desktopSort={sortValue.current}
                              />
                            </ErrorBoundary>
                          </>
                        )}
                      </div>
                      {isAppliedRefinementsAvailable && (
                        <Row className="sui-pl-3">
                          <Col>
                            <ErrorBoundary name="top-results-applied">
                              <ResultsApplied
                                onAppliedChange={onDimensionsChange}
                                onDisplay={onDisplay}
                                onDisplayChange={onDisplayChange}
                                hideResultsCount={viewPrimaryFilters}
                                isFulfillmentRedesignEnabled={isFulfillmentRedesignEnabled}
                                isGetItFastOnPlp={isGetItFastOnPlp}
                              />
                            </ErrorBoundary>
                          </Col>
                        </Row>
                      )}
                    </>
                  )}

                {isBrowse && isGetItFastOnPlp && (
                  <Row className="sui-pb-3 sui-mt-2 sui-mb-5">
                    <Col>
                      <ErrorBoundary name="fit-compatibility">
                        <FitCompatibility
                          type="PLP"
                          imageCacheEnabled
                          navParam={nValue}
                          data={data}
                        />
                      </ErrorBoundary>
                    </Col>
                  </Row>
                )}
                <ResultsWrapped>
                  <div className="sui-grid sui-pl-3 sui-w-full sui-mt-2" ref={resultsDimensionsRef}>
                    {!products?.length && loading && isSearch && (
                      <ProductPodPlaceholder podsCount={12} />
                    )}
                    {!!hydrators?.length && (
                      <QueryProvider
                        dataSource="searchNav"
                        defaultVariables={{
                          storeId: loadedStore.current?.storeId,
                          skipKPF: false,
                          skipSubscribeAndSave: true,
                          skipInstallServices: false,
                          zipCode: loadedStore.current?.zipCode,
                          isBrandPricingPolicyCompliant:
                          localizedAndClientLoaded.current
                            ? isCustomerIdentified
                            : false
                        }}
                        skip={skipFn}
                        mounted={mountedFn}
                        cacheKey="search-product-pods-dt"
                      >
                        {hydrators.map((group, hydratorIndex) => {
                          if (!group?.length) return null;
                          return (
                            <Hydrator
                              afterHydrate={() => { setIsHydrated(true) }}
                              id={`browse-search-pods${hydratorIndex + 1}`}
                              className={browseSearchClass}
                              scrollBuffer={CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER}
                              patch={!hydratorIndex}
                              key={`hydrator-${hydratorIndex}`}
                            >
                              <HoistedProductPodGroup
                                DetailsPod={CustomProductPodDetails}
                                keyword={keyword}
                              >
                                {group?.map((renderPod, itemIndex, productArray) => {
                                  const { itemId, canonicalUrl, parentId } = renderPod?.identifiers || {};
                                  const isBundleProduct = renderPod?.bundleFlag || false;
                                  if (!itemId) {
                                    return (
                                      <div
                                        className="sui-grid"
                                        key={`placeholder-${itemIndex}`}
                                      >
                                        <RectShape
                                          style={{ width: '100%', height: '250px', marginBottom: '10px' }}
                                          color="#E0E0E0"
                                        />
                                        <TextBlock rows={7} color="#E0E0E0" />
                                      </div>
                                    );
                                  }
                                  const showContentCard = hydratorIndex * MAX_PODS_TO_HYDRATE.DESKTOP + itemIndex
                                    === CATEGORY_CARD_POSITION_INDEX;

                                  const showInGridBannerInPods = (
                                    (hydratorIndex * MAX_PODS_TO_HYDRATE.DESKTOP) + itemIndex === inGridBannerIndex
                                  );

                                  const podPosition = hydratorIndex * MAX_PODS_TO_HYDRATE.DESKTOP + itemIndex;

                                  const showDiscoveryZone1 = hydratorIndex * MAX_PODS_TO_HYDRATE.DESKTOP + itemIndex
                                    === containerIndex.zone1;

                                  const showDiscoveryZone2 = hydratorIndex * MAX_PODS_TO_HYDRATE.DESKTOP + itemIndex
                                    === containerIndex.zone2;

                                  return (
                                    <ErrorBoundary
                                      id={`plp-product-pod__${itemIndex}`}
                                      index={itemIndex}
                                      key={`${itemId}-${itemIndex}`}
                                    >
                                      {showContentCard
                                        && (
                                          <div className={categoryCardClass}>
                                            <CategoryCards
                                              key={`category-card-${itemIndex}`}
                                              data={dataForCategoryCards}
                                              loading={loading}
                                              error={error}
                                              storeId={loadedStore.current?.storeId}
                                              keyword={keyword}
                                              navParam={nValue}
                                            />
                                          </div>
                                        )}
                                      {showInGridBannerInPods
                                        && isExperienceResolved
                                        && isInGridBannerEnabled
                                        && !isInGridBannerBottom
                                        && (
                                          <div className={wrapperClass}>
                                            <ErrorBoundary name="sponsored-ingrid-nucleus">
                                              <SponsoredInGridBanner
                                                browserId={thdCustomer?.mcvisID || ''}
                                                pageContext={{
                                                  label: 'browse-search',
                                                  data,
                                                  keyword,
                                                  isCategory,
                                                  isBrowse,
                                                  isSearch
                                                }}
                                                dynamic={{
                                                  pageType
                                                }}
                                                hydrator={{
                                                  className: 'grid',
                                                  scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                                                }}
                                              />
                                            </ErrorBoundary>
                                          </div>
                                        )}
                                      {isDiscoveryZonesEligible
                                        && showDiscoveryZone1
                                        && renderDiscoveryZones('1')}

                                      {((isBrowse && isDiscoveryZonesEnabled)
                                        || (isSearch && isDiscoveryZonesSearchEnabled))
                                        && showDiscoveryZone2
                                        && renderDiscoveryZones('2')}
                                      {layoutStyle === 'List' ? (
                                        <div className="lg:sui-col-span-2 xl:sui-col-span-1 sui-mb-2">
                                          <ImpressionProvider
                                            key={itemId}
                                            data={impProductPodData(layoutID)}
                                          >
                                            <CustomProductPodTwoByTwoColumn
                                              itemId={itemId}
                                              onClick={handleProductClick}
                                              onHover={prefetchProductInfo}
                                              hoverDelay={productPodHoverDelay}
                                              storeId={loadedStore.current?.storeId}
                                              position={podPosition}
                                              onCompare={onCompare}
                                              onSelectedForCompare={onSelectedForCompare}
                                              selectedForCompare={selectedForCompare}
                                              hasInStoreFilter={!!hasInStoreFilter}
                                              itemRecsIndex={itemIndex}
                                              impProductPodData={impProductPodData(layoutID)}
                                              isB2BCustomer={isB2BCustomer}
                                              url={canonicalUrl}
                                              isBundleProduct={isBundleProduct}
                                              hasSameDayFilter={hasSameDayFilter}
                                              isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
                                            />
                                          </ImpressionProvider>
                                        </div>
                                      ) : (
                                        <div className="sui-grid">
                                          <ImpressionProvider
                                            key={itemId}
                                            data={impProductPodData(layoutID)}
                                          >
                                            <CustomProductPodResponsive
                                              itemId={itemId}
                                              parentId={parentId}
                                              storeId={storeId}
                                              position={podPosition}
                                              itemRecsIndex={itemIndex}
                                              hasInStoreFilter={!!hasInStoreFilter}
                                              impProductPodData={impProductPodData(layoutID)}
                                              showProjectQuantity
                                              isB2BCustomer={isB2BCustomer}
                                              viewListPods={viewListPods}
                                              hasSameDayFilter={hasSameDayFilter}
                                              url={canonicalUrl}
                                              onHover={prefetchProductInfo}
                                              onCompare={onCompare}
                                              onSelectedForCompare={onSelectedForCompare}
                                              selectedForCompare={selectedForCompare}
                                              isBundleProduct={isBundleProduct}
                                              isFulfillmentFilterAndMsgEnabled={isFulfillmentFSEnabled}
                                              products={productArray}
                                              isViewSimilarButtonEnabled={(isViewSimilarTopButtonEnabled
                                                || isViewSimilarBottomButtonEnabled)
                                                ? { isViewSimilarTopButtonEnabled,
                                                  isViewSimilarBottomButtonEnabled,
                                                  productArray }
                                                : null}
                                            />
                                          </ImpressionProvider>
                                        </div>
                                      )}
                                    </ErrorBoundary>
                                  );
                                })}
                              </HoistedProductPodGroup>
                            </Hydrator>
                          );
                        })}
                      </QueryProvider>
                    )}
                  </div>
                </ResultsWrapped>
                {isExperienceResolved
                  && isInGridBannerBottom
                  && isInGridBannerEnabled
                  && (
                    <Col className="sui-pr-0">
                      <ErrorBoundary name="sponsored-ingrid-nucleus">
                        <SponsoredInGridBanner
                          browserId={thdCustomer?.mcvisID || ''}
                          pageContext={{
                            label: 'browse-search',
                            data,
                            keyword,
                            isCategory,
                            isBrowse,
                            isSearch
                          }}
                          dynamic={{
                            pageType
                          }}
                          hydrator={{
                            className: 'grid',
                            scrollBuffer: HYDRATION_STANDARD_SCROLL_BUFFER
                          }}
                        />
                      </ErrorBoundary>
                    </Col>
                  )}
                <Row className="u--paddingTop u--paddingBottom">
                  <Col>
                    <ErrorBoundary name="compare-tray">
                      <CompareTray
                        itemIds={selectedForCompare}
                        onRemove={onSelectedForCompare}
                        onCompare={onCompare}
                      />
                    </ErrorBoundary>
                  </Col>
                </Row>
                <DynamicComponent pageType={pageType}>
                  <Row>
                    <Col>
                      <ErrorBoundary name="results-pagination">
                        <ResultsPagination
                          pageSize={searchNavPropsRef.current.pageSize}
                          onPageChange={onPaginate}
                          path={pathWithParams}
                        />
                      </ErrorBoundary>
                    </Col>
                  </Row>
                </DynamicComponent>
                {!hideFeedback && (
                  <DynamicComponent pageType={pageType}>
                    <Hydrator
                      id="feedback"
                      className="sui-grid sui-w-full"
                      scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
                    >
                      <div>
                        <ErrorBoundary name="search-feedback">
                          <div className="sui-py-3 sui-pl-3">
                            <ImpressionProvider
                              key="SearchFeedback"
                              data={{
                                id: '',
                                component: 'SearchFeedback',
                                name: 'SearchFeedback',
                                type: 'content'
                              }}
                            >
                              <SearchFeedback />
                            </ImpressionProvider>
                          </div>
                        </ErrorBoundary>
                      </div>
                    </Hydrator>
                  </DynamicComponent>
                )}
              </Col>
            </Row>
          )}
        </ProductResults>
        {!hybridLoading && !isBrandPage && !hideSponsoredCarousel && (
          <RetailMediaCarousel
            browserId={thdCustomer?.mcvisID}
            userId={thdCustomer?.svocID || thdCustomer?.userID || thdCustomer?.guestUserID}
            breadCrumbs={data?.searchModel?.taxonomy?.breadCrumbs}
            categorySourceId={data?.searchModel?.metadata?.categoryID}
            pageContext={{
              label: 'browse-search',
              data: {
                itemIds: getItemIds(products),
              },
              keyword,
              isCategory,
              isBrowse,
              isSearch
            }}
            nValue={nValue}
            showSponsoredCarousel
            dynamic={{
              pageType
            }}
            hydrator={{
              className: 'grid zone-card',
              delay: 1500,
              scrollBuffer: CAROUSEL_HYDRATION_STANDARD_SCROLL_BUFFER
            }}
          />
        )}
        {!hybridLoading && !isBrandPage && !discoveryZonesFallback && (
          <PlpRecsRenderPlaceholder
            requestKey={RECS_REQUEST_KEY}
            keyword={keyword}
            isBrowse={isBrowse}
            itemIds={getItemIds(products)}
            impressionData={impPLPRecs()}
            browseNValue={filterBrowseRecs ? nValue : ''}
          />
        )}
        {!hybridLoading && (
          <PlpRvRenderPlaceholder isCategory={isCategory} isSearch={isSearch} />
        )}

        {isSearch && (
          <ErrorBoundary name="seo-related-search">
            <RelatedSearch
              dynamic={{
                pageType
              }}
              hydrator={{
                className: 'sui-w-full sui-px-2 2xl:sui-px-0 sui-my-4 empty:sui-my-0'
              }}
              navParam={nValue}
              searchData={data}
              searchError={error}
              searchLoading={loading}
              storeId={initialLoadedStoreId.current}
            />
          </ErrorBoundary>
        )}

        {!isSearch && (
          <>
            <ErrorBoundary name="emt-questions-and-answers">
              <EmtQuestionsAndAnswers
                nValue={nValue}
                dynamic={{
                  pageType
                }}
                hydrator={{
                  className: 'sui-px-2 2xl:sui-px-0 sui-my-4'
                }}
              />
            </ErrorBoundary>

            <DynamicComponent pageType={pageType}>
              <Hydrator
                id="browse-emt-product-category-content"
                className="grid"
                waitFor={[EmtGeneratedContentLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <Row>
                  <Col>
                    <ErrorBoundary name="emt-generated-content-product-content">
                      <EmtGeneratedContent type="productCategoryContent" nvalue={nValue} />
                    </ErrorBoundary>
                  </Col>
                </Row>
              </Hydrator>
            </DynamicComponent>

            <DynamicComponent pageType={pageType}>
              <Hydrator
                id="browse-emt-related-categories"
                className="grid"
                waitFor={[EmtGeneratedContentLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <Row>
                  <Col>
                    <ErrorBoundary name="emt-generated-content-related-categories">
                      <EmtGeneratedContent type="relatedCategories" nvalue={nValue} nopadding />
                    </ErrorBoundary>
                  </Col>
                </Row>
              </Hydrator>
            </DynamicComponent>

            <ErrorBoundary name="seo-related-search">
              <RelatedSearch
                dynamic={{
                  pageType
                }}
                hydrator={{
                  className: 'sui-w-full sui-px-2 2xl:sui-px-0 sui-my-4 empty:sui-my-0'
                }}
                navParam={nValue}
                searchData={data}
                searchError={error}
                searchLoading={loading}
                storeId={initialLoadedStoreId.current}
              />
            </ErrorBoundary>

            <ErrorBoundary name="seo-related-products">
              <RelatedProducts
                dynamic={{
                  pageType
                }}
                hydrator={{
                  className: 'sui-px-2 2xl:sui-px-0 sui-my-4 empty:sui-my-0'
                }}
                navParam={nValue}
                storeId={initialLoadedStoreId.current}
              />
            </ErrorBoundary>

            <DynamicComponent pageType={pageType}>
              <Hydrator
                id="browse-emt-links"
                className="grid"
                waitFor={[EmtLinksLoad]}
                scrollBuffer={HYDRATION_STANDARD_SCROLL_BUFFER}
              >
                <Row className="u--paddingTop u--paddingBottom">
                  <Col nopadding>
                    <EmtLinks path={pathWithParams} />
                  </Col>
                </Row>
              </Hydrator>
            </DynamicComponent>
          </>
        )}
      </Row>
    </>
  );
};

BrowseAndSearch.displayName = 'BrowseAndSearchPage';

BrowseAndSearch.themeProps = {
  hideSponsoredCarousel: bool,
  hideSponsoredBanner: bool,
  hideSponsoredSkyscraperBanner: bool,
  hideFFMFilters: bool,
  hideFeedback: bool,
};

BrowseAndSearch.defaultThemeProps = {
  hideSponsoredCarousel: false,
  hideSponsoredBanner: false,
  hideSponsoredSkyscraperBanner: false,
  hideFFMFilters: false,
  hideFeedback: false,
};

BrowseAndSearch.propTypes = {
  pageType: string.isRequired,
  prefetchPip: string,
  productPodHoverDelay: number,
  storeId: string,
  storeZip: string,
  isLocalized: bool,
  pathname: string.isRequired,
  instance: shape({}).isRequired,
  customerType: string.isRequired
};

BrowseAndSearch.defaultProps = {
  prefetchPip: null,
  productPodHoverDelay: 500,
  storeId: null,
  storeZip: null,
  isLocalized: false,
};

const ProductSearchDataModel = {
  searchModel: params({ keyword: stringType(), navParam: stringType() }).shape({
    products: params({ startIndex: numberType() }).arrayOf(CustomProductPodResponsive.dataModel.product),
  }),
};

BrowseAndSearch.dataModel = extend(
  {
    searchModel: params().shape({
      metadata: shapeType({
        hasPLPBanner: boolType(),
        categoryID: stringType(),
        analytics: shapeType({
          semanticTokens: stringType(),
          dynamicLCA: stringType(),
        }),
      }),
      products: params().arrayOf(
        shapeType({
          identifiers: shapeType({
            storeSkuNumber: client(stringType()),
          }),
          installServices: params({ storeId: stringType(), zipCode: stringType() }).shape({
            scheduleAMeasure: boolType(),
            gccCarpetDesignAndOrderEligible: boolType(),
          }),
        })
      ),
    }),
  },
  ResultsDimensions,
  ResultsFilterAndSort,
  ResultsFilterAndSortDrawer,
  BrowseSearchRedirector,
  BrowseSearchMetadata,
  Breadcrumbs,
  SponsoredBanner,
  ProductCategoryVisnav,
  AisleBay,
  CategoryCards,
  CompareTray,
  DiscoveryZonesWrapper,
  EmtGeneratedContentModel,
  EmtLinksModel,
  EmtQuestionsAndAnswers,
  RelatedProducts,
  RelatedSearch,
  NoResultsFound,
  productDefaultResultsDataModel,
  PlpRecsRenderPlaceholder,
  PlpRvRenderPlaceholder,
  BuyItAgain,
  HybridContentfulWidgets.dataModel,
  // This must come last
  ProductSearchDataModel
);